/*
 * PDP related products picker.
 * Switches between separate products (product.metafields.pdp.related_products).
 *
 * Each item is a separate product, so the picker opens that product's page (a normal page load:
 * price, stock, subscription widget, reviews and all app blocks initialise exactly as on a direct visit).
 * To make the switch feel in place, the picker's position on screen is kept:
 * it is stored in sessionStorage just before leaving and restored on the next product page.
 * (All items are visible at once - they wrap onto more rows, there is no horizontal scroll.)
 */
if (!customElements.get('pdp-related-picker')) {
  customElements.define(
    'pdp-related-picker',
    class PdpRelatedPicker extends HTMLElement {
      static storageKey = 'pdp-related-picker-position';

      connectedCallback() {
        this.onChange = this.onChange.bind(this);
        this.addEventListener('change', this.onChange);

        const saved = this.readSavedPosition();
        if (saved) this.restorePagePosition(saved);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
      }

      readSavedPosition() {
        try {
          const raw = sessionStorage.getItem(PdpRelatedPicker.storageKey);
          sessionStorage.removeItem(PdpRelatedPicker.storageKey);
          if (!raw) return null;
          const saved = JSON.parse(raw);
          // only right after a switch made with this picker, to this page
          if (!saved || Date.now() - saved.time > 15000 || saved.path !== window.location.pathname) return null;
          return saved;
        } catch (error) {
          return null;
        }
      }

      restorePagePosition(saved) {
        if (typeof saved.top !== 'number') return;
        let lastY = null;
        const apply = () => {
          // stop as soon as the visitor scrolls on their own
          if (lastY !== null && Math.abs(window.scrollY - lastY) > 2) return;
          const top = this.getBoundingClientRect().top;
          window.scrollTo(window.scrollX, Math.max(0, window.scrollY + top - saved.top));
          lastY = window.scrollY;
        };
        apply();
        requestAnimationFrame(apply);
        // images above the picker (mobile gallery) can still change the layout
        if (document.readyState !== 'complete') window.addEventListener('load', apply, { once: true });
      }

      onChange(event) {
        const input = event.target;
        const url = input?.dataset?.productUrl;
        if (!url) return;

        this.updateLabel(input);

        try {
          sessionStorage.setItem(
            PdpRelatedPicker.storageKey,
            JSON.stringify({
              path: new URL(url, window.location.origin).pathname,
              top: this.getBoundingClientRect().top,
              time: Date.now(),
            })
          );
        } catch (error) {
          // storage unavailable - the page simply opens at the top
        }

        window.location.href = url;
      }

      updateLabel(input) {
        const selectedValue = this.querySelector('[data-selected-value]');
        if (selectedValue) selectedValue.textContent = input.dataset.shortName || input.value;

        const colorDescription = this.querySelector('.color-description');
        if (colorDescription) colorDescription.textContent = input.dataset.colorDescription || '';
      }
    }
  );
}
